import React, {
  createContext,
  useEffect,
  useState,
  useCallback,
  useContext,
} from "react";
import { handleNetworkError } from "../../utils.js";
import { User } from "../../service.js";
import { useLocation, useNavigate } from "react-router-dom";

export const UserContext = createContext();

const UserProvider = ({ children }) => {
  const [userData, setUserData] = useState({});
  const [status, setStatus] = useState("init");
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    console.log('user data changed:', userData);
  }, [userData]);

  const refreshUserData = useCallback(async () => {
    // Skip API call for auth-related pages and landing pages
    const skipApiRoutes = [
      "/login",
      "/signup",
      "/forgot-password",
      "/",
      "/landlords",
      "/property-managers",
      "/short-term-rentals",
      "/homeowners",
      "/vendors",
      "/free-ebook",
      "/free-ebook/thank-you",
      "/free-ebook/download",
      "/home-maintenance-assessment",
      "/home-maintenance-assessment/thank-you",
    ];

    if (skipApiRoutes.includes(location.pathname)) {
      setStatus("ready");
      return;
    }

    setStatus("loading");
    try {
      const response = await User.getMe();
      setUserData(response.data);
      setStatus("ready");
    } catch (error) {
      setUserData(false);
      handleNetworkError(error, false, false);
      setStatus("ready");
    }
  }, [location.pathname]);

  useEffect(() => {
    refreshUserData();
  }, [refreshUserData, location.pathname]);

  // Listen for token expiration events
  useEffect(() => {
    const handleTokenExpired = () => {
      console.log("Token expired, navigating to login");
      setUserData({});
      setStatus("ready");

      // Only navigate to login if we're not already on an auth page
      const authPages = ["/login", "/signup", "/forgot-password"];
      if (!authPages.includes(location.pathname)) {
        navigate("/login", { replace: true });
      }
    };

    window.addEventListener("tokenExpired", handleTokenExpired);

    return () => {
      window.removeEventListener("tokenExpired", handleTokenExpired);
    };
  }, [navigate, location.pathname]);

  const values = {
    isLoading: status !== "ready",
    userData,
    setUserData,
    refreshUserData,
  };

  return <UserContext.Provider value={values}>{children}</UserContext.Provider>;
};

// Custom hook to use UserContext
export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
};

export default UserProvider;
// export const useDummy = () => {
//   return React.useContext(UserContext);
// };
